import { useState } from "react";
import { Globe, Scale, LineChart, Network, Cpu, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionShell, SectionHead, FeatureCard, IconChip } from "@/components/site/Primitives";

const FORCES = [
  { icon: Globe, tint: "blue", title: "Geopolitical Complexity", items: ["Sanctions", "Trade restrictions", "Regional disruption", "Fragmented supply chains"] },
  { icon: Scale, tint: "purple", title: "Regulatory Complexity", items: ["Environmental requirements", "Trade compliance", "Reporting obligations", "Financial transparency"] },
  { icon: LineChart, tint: "gold", title: "Commercial Complexity", items: ["Freight volatility", "Fuel-price changes", "Charter complexity", "Margin pressure"] },
  { icon: Network, tint: "blue", title: "Digital Transformation", items: ["Increasing data volumes", "Disconnected enterprise systems", "Cloud platforms", "APIs and digitised workflows"] },
  { icon: Cpu, tint: "blue", title: "AI Maturity", items: ["Improved extraction", "Advanced reasoning", "Scenario analysis", "Explainable decision support"] },
];

function MobileWhyNowAccordion({ force, isOpen, onToggle }) {
  const { icon: Icon, tint, title, items } = force;
  return (
    <div
      className="rounded-2xl border border-natsu-navy/12 bg-white transition-colors"
      data-testid={`why-now-accordion-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="flex items-center gap-3.5">
          <IconChip icon={Icon} tint={tint} className="flex-shrink-0" />
          <span className="font-display text-[15px] leading-snug text-natsu-navy">{title}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-natsu-slate transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="min-h-0">
          <div className="border-t border-natsu-navy/[0.08] px-5 pb-5 pt-4">
            <ul className="space-y-2">
              {items.map((it) => (
                <li key={it} className="flex items-center gap-2.5 text-[13px] text-natsu-navy/75">
                  <span className="h-1 w-1 flex-shrink-0 rounded-full bg-natsu-gold" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WhyNow() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <SectionShell id="why-now" tone="mist">
      <div className="max-w-3xl">
        <SectionHead
          label="Why Now"
          labelColor="blue"
          title="Maritime commerce is entering an intelligence transition."
          intro="Geopolitical, regulatory, commercial and technological forces are converging while executive decision environments become more complex."
        />
      </div>

      {/* Mobile: accordions (single-open) */}
      <div className="mt-10 flex flex-col gap-3 sm:hidden">
        {FORCES.map((f, i) => (
          <MobileWhyNowAccordion
            key={f.title}
            force={f}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
          />
        ))}
      </div>

      {/* Tablet & Desktop: expanded cards */}
      <div className="mt-14 hidden grid-cols-2 gap-4 sm:grid lg:grid-cols-5">
        {FORCES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.06}>
            <FeatureCard icon={f.icon} tint={f.tint} title={f.title} examples={f.items} />
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p className="mt-14 max-w-[960px] font-display text-xl leading-relaxed text-natsu-navy sm:text-2xl">
          Technology has evolved. Data is everywhere.
          <br className="hidden lg:inline" />{" "}
          <span className="lg:block">Executive intelligence remains scarce.</span>
        </p>
      </Reveal>
    </SectionShell>
  );
}
